// useChangePassword (contract 06 §9) — the ONE public seam for the AUTHENTICATED settings password
// change (`POST /auth/settings/change_password/`). It exists because contract 06 §4 keeps `AuthApi`
// and `AuthService` feature-internal: module 22's profile pane cannot call them, so it calls this.
// Deliberately flow-less — there is no orchestration left to do here (no store write, no second
// call, no navigation), so a flow would be an empty pass-through; the surrounding work (success
// toast, `has_password` refresh) belongs to module 22's own ChangePasswordFlow.
//
// The error surface is the point (module 22's defect D18): the legacy rendered "Please check your
// current password." for EVERY failure — offline, 500 and an actually-wrong current password alike
// (v3 `ChangePasswordSection.vue:90`). Here the typed error reaches the caller intact: `errorCode`
// says WHICH field is at fault, `error` is the i18n key to render. A transport or server failure
// stays `unknown` and keeps ApiError's own `errors.http.<kind>` key, so the form can tell "your
// current password is wrong" apart from "we could not reach the server".
import { ref, type Ref } from 'vue'

import { AuthError, type AuthErrorCode } from '../models/auth.error'
import type { ChangeSettingsPasswordPayload } from '../models/auth.types'
import { authService } from '../service/AuthService'

export interface SettingsPasswordChange {
  /** OMIT the key entirely for a user who has no password yet (`has_password === false`, module
   *  07) — never pass `''`. An empty string is a *wrong* current password to the backend. */
  oldPassword?: string
  password: string
  confirmPassword: string
}

export interface UseChangePassword {
  pending: Ref<boolean>
  /** i18n KEY of the last failure (never raw text); cleared on the next attempt. */
  error: Ref<string | null>
  /** The same failure as a machine code, so the caller binds it to one field instead of showing
   *  one catch-all sentence: `invalidOldPassword` → the current-password field, `weakPassword` →
   *  the new-password field, `unknown` → a form-level message read from `error`. */
  errorCode: Ref<AuthErrorCode | null>
  /** Resolves `true` on success, `false` with `error`/`errorCode` set on any failure. The caller
   *  owns what happens next — v4 stays on the pane (v3 redirected to Summary). */
  changePassword: (input: SettingsPasswordChange) => Promise<boolean>
}

export function useChangePassword(): UseChangePassword {
  const pending = ref(false)
  const error = ref<string | null>(null)
  const errorCode = ref<AuthErrorCode | null>(null)

  async function changePassword(input: SettingsPasswordChange): Promise<boolean> {
    pending.value = true
    error.value = null
    errorCode.value = null
    try {
      const payload: ChangeSettingsPasswordPayload = {
        password: input.password,
        confirm_password: input.confirmPassword,
      }
      // Built conditionally, not spread with `undefined`: the key must be absent from the JSON.
      if (input.oldPassword !== undefined) payload.old_password = input.oldPassword
      await authService.changeSettingsPassword(payload)
      return true
    } catch (e) {
      const authError = AuthError.from(e)
      error.value = authError.i18nKey
      errorCode.value = authError.code
      return false
    } finally {
      pending.value = false
    }
  }

  return { pending, error, errorCode, changePassword }
}
